/**
 * @file util/utils.ts
 * @description 通用工具方法：下载、样式合并、时间与大小格式化、Base64/Blob/ArrayBuffer 转换、
 * 模板变量提取与匹配、ID/标题替换等。
 */
import { BlockEnum } from '@/components/workflow/types'
import axios, { customFetch } from '@/controllers/request'
import clsx, { ClassValue } from 'clsx'
import moment from 'moment'
import { twMerge } from 'tailwind-merge'
import { APITemplateType } from '../types/api'
import { checkUpperWords } from '../utils'

/** 将类名数组合并为字符串（过滤空值） */
export function classNames(...classes: Array<string>): string {
  return classes.filter(Boolean).join(' ')
}

/** 请求并下载二进制文件（blob） */
export function downloadFile(url, label) {
  return axios
    .get(url, { responseType: 'blob' })
    .then((res: any) => {
      const blob = new Blob([res.data])
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = label
      link.click()
      URL.revokeObjectURL(link.href)
    })
    .catch(console.error)
}

/** 直接通过超链接触发浏览器下载（同域/已开放） */
export const downloadFileByUrl = function (fileUrl, fileName) {
  const a = document.createElement('a')
  a.href = fileUrl
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** fetch 下载文件并保存（跨域需服务器允许） */
export async function downloadFileByUrl2(fileUrl, fileName) {
  try {
    const response = await customFetch(fileUrl)
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName) // or any other filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link) // clean up the element
    window.URL.revokeObjectURL(url) // cleanup the URL object
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

/** 下载任意 JSON 内容为本地文件 */
export function downloadJson(content) {
  const jsonStr = JSON.stringify(content)
  const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(jsonStr)}`

  const link = document.createElement('a')
  link.href = jsonString
  link.download = 'sample.json'

  link.click()
}

/** tailwind-merge + clsx 的组合：去重合并 className */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// 交集
/** 多数组求交集（以首个数组为基准） */
export function intersectArrays(...arrays) {
  if (arrays.length === 0) {
    return []
  }

  // 使用第一个数组作为基准
  const baseArray = arrays[0]

  // 过滤出基准数组中的元素，这些元素在其他所有数组中都存在
  const intersection = baseArray.filter(element => {
    return arrays.every(array => array.includes(element))
  })

  return intersection
}

// 时间戳转换 天时分秒（dhms）
/**
 * 毫秒转 dd/hh/mm/ss 格式字符串
 * @param ms 毫秒
 * @param format 模板（dd/hh/mm/ss）
 */
export function formatMilliseconds(ms: number, format: string): string {
  const secondsInMillisecond = 1
  const minutesInMillisecond = secondsInMillisecond * 60
  const hoursInMillisecond = minutesInMillisecond * 60
  const daysInMillisecond = hoursInMillisecond * 24

  const days = Math.floor(ms / daysInMillisecond)
  const remainingHours = ms % daysInMillisecond
  const hours = Math.floor(remainingHours / hoursInMillisecond)
  const remainingMinutes = remainingHours % hoursInMillisecond
  const minutes = Math.floor(remainingMinutes / minutesInMillisecond)
  const remainingSeconds = remainingMinutes % minutesInMillisecond
  const seconds = Math.floor(remainingSeconds / secondsInMillisecond)

  let formattedString = format.replace('dd', days.toString())
  formattedString = formattedString.replace('hh', hours.toString())
  formattedString = formattedString.replace('mm', minutes.toString())
  formattedString = formattedString.replace('ss', seconds.toString())

  // Remove any extra spaces
  // formattedString = formattedString.replace(/\s+/g, ' ').trim();

  return formattedString
}

/** 蛇形/下划线/短横文本转 Title Case（保留缩写规则） */
export function toTitleCase(str: string | undefined): string {
  if (!str) return ''
  const result = str
    .split('_')
    .map((word, index) => {
      if (index === 0) {
        return checkUpperWords(word[0].toUpperCase() + word.slice(1).toLowerCase())
      }
      return checkUpperWords(word.toLowerCase())
    })
    .join(' ')

  return result
    .split('-')
    .map((word, index) => {
      if (index === 0) {
        return checkUpperWords(word[0].toUpperCase() + word.slice(1).toLowerCase())
      }
      return checkUpperWords(word.toLowerCase())
    })
    .join(' ')
}

/** 获取模板字段的展示标题（display_name/name/key 顺序） */
export function getFieldTitle(template: APITemplateType, templateField: string): string {
  return template[templateField].display_name
    ? template[templateField].display_name!
    : template[templateField].name
    ? toTitleCase(template[templateField].name!)
    : toTitleCase(templateField)
}

/* 转换类型 number123转中文一二三 */
/** 数字转中文大写（简单版） */
export function digitToChinese(digit) {
  const chineseNums = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']
  const chineseUnits = ['', '十', '百', '千', '万', '十', '百', '千', '亿']
  let str = ''
  let unitIndex = 0

  for (let i = digit.toString().length - 1; i >= 0; i--) {
    const currentDigit = digit.toString()[i]
    str = chineseNums[currentDigit] + (currentDigit !== '0' ? chineseUnits[unitIndex] : '') + str

    // 处理特殊情况，避免连续的“零”
    if (str.startsWith('零') && str.length > 1 && str[1] !== '零') {
      str = str.substring(1)
    }

    unitIndex++
  }

  // 移除开头的“零”
  str = str.replace(/^零+/, '')

  return str
}

/** 字节数格式化为 B/KB/MB（两位小数） */
export function fileSizeFormat(bytes) {
  if (!bytes) {
    return ''
  }
  if (bytes < 1024) {
    return bytes + ' B'
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + ' KB'
  }
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

/** Base64 PNG 转 Blob */
export function convertBase64ToBlob(base64Data) {
  const byteCharacters = atob(base64Data)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  // 创建Blob对象
  const blob = new Blob([byteArray], { type: 'image/png' })
  return blob
}

/** Blob 转 ArrayBuffer（回调式） */
export function blobToArrayBuffer(blob, callback) {
  const fileReader = new FileReader()
  fileReader.onload = function () {
    callback(this.result)
  }
  fileReader.readAsArrayBuffer(blob)
}

/**
 *
 * @param value
 * @description 4位数使用千分符号隔开
 * @returns string
 */
/** 数字千分位格式化（字符串/数字均可） */
export const formatMaxValue = value => {
  if (value == null) return value
  if (Number.isNaN(+value)) return value
  return `${value}`.replace(/(\d{1,3})(?=(\d{3})+(?:$|\.))/g, '$1,')
}

// 将ArrayBuffer转换为Base64
/** ArrayBuffer 高效分块转 Base64 */
export function arrayBufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000 // 32KB
  let index = 0

  while (index < bytes.length) {
    const chunk = bytes.subarray(index, Math.min(bytes.length, index + chunkSize))
    binary += String.fromCharCode.apply(null, chunk)
    index += chunkSize
  }

  return btoa(binary)
}

/* 正则获取{{}} 模型输入内容返回对于id and variable */
/**
 * 从字符串中提取 {{#id.var#}} 模板变量
 * @returns 形如 { id, variable } 的数组
 */
export function extractTemplateVariables(inputString: string): { id: string; variable: string }[] {
  // 定义正则表达式来匹配 {{#...#}} 模式
  const regex = /\{\{#(\w+)\.(\w+)#\}\}/g
  let match
  const result: { id: string; variable: string }[] = []

  // 使用 exec 方法循环查找所有匹配项
  while ((match = regex.exec(inputString)) !== null) {
    // match[1] 是 id, match[2] 是 variable
    if (match[1] && match[2]) {
      result.push({ id: match[1], variable: match[2] })
    }
  }

  return result
}
/** 模板变量基础信息 */
export interface VariableInfo {
  id: string
  variable: string
  tempName: string
}

/** 节点输入变量定义 */
export interface NodeVariable {
  id: string
  type: string
  label?: string
  value?: any
  default?: any
  required?: boolean
  variable: string
  max_length?: number
  [key: string]: any
}

/** 节点输出变量定义 */
export interface NodeOutput {
  type: string
  value: any
  variable: string
  [key: string]: any
}

/** 节点简化结构（匹配变量使用） */
export interface Node {
  id: string
  variables?: NodeVariable[]
  outputList?: NodeOutput[]
  [key: string]: any
}

/* 匹配flowVariables 重置当前模块的startVariables */
/** 将模板变量匹配到节点的 variables 或 outputList（去重） */
export function matchVariablesToNodes(
  templateVars: VariableInfo[],
  nodes: { [key: string]: Node }
): (NodeVariable | NodeOutput)[] {
  const matchedVariables: (NodeVariable | NodeOutput)[] = []

  templateVars.forEach(({ id, variable }) => {
    const node = nodes[id]
    if (node) {
      // 检查 variables 数组 = 开始节点
      if (node.variables) {
        const varMatch = node.variables.find(v => v.variable === variable)
        if (varMatch) {
          matchedVariables.push(varMatch)
        }
      }

      // 如果没有找到，则检查 outputList 数组 = 大模型节点
      if (node.outputList) {
        const outputMatch = node.outputList.find(o => o.variable === variable)
        if (outputMatch) {
          matchedVariables.push(outputMatch)
        }
      }
    }
  })

  // 去重
  return matchedVariables.filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
}

/**
 * 变量匹配增强版：
 * - 支持 Start 节点嵌套对象的路径解析（a.b.c）
 * - 数组类型显示为 array<elementType>
 */
export function matchVariablesToNodesV2(
  templateVars: VariableInfo[],
  nodes: { [key: string]: Node }
): (NodeVariable | NodeOutput)[] {
  const matchedVariables: (NodeVariable | NodeOutput)[] = []

  templateVars.forEach(({ id, variable, tempName }) => {
    const node = nodes[id]
    if (node) {
      // 检查 variables 数组 = 开始节点
      if (node.type === BlockEnum.Start && node.variables) {
        // 处理嵌套路径，如 "aw.afef.1"
        const pathParts = variable.split('.')
        let currentVar = node.variables.find(v => v.variable === pathParts[0]) as any

        if (currentVar && pathParts.length > 1) {
          // 递归查找嵌套属性
          for (let i = 1; i < pathParts.length; i++) {
            const pathPart = pathParts[i]
            if (currentVar.type === 'object' && currentVar.complexConfig?.properties) {
              const nestedVar = Object.values(currentVar.complexConfig.properties).find(
                (prop: any) => prop.variable === pathPart
              )
              if (nestedVar) {
                currentVar = nestedVar
              } else {
                currentVar = null
                break
              }
            } else {
              currentVar = null
              break
            }
          }
        }

        if (currentVar) {
          // 为 array 类型添加正确的显示格式
          if (currentVar.type === 'array' && currentVar.complexConfig?.elementType) {
            currentVar = {
              ...currentVar,
              type: `${currentVar.type}<${currentVar.complexConfig.elementType}>`
            }
          }

          matchedVariables.push({
            ...currentVar,
            nodeId: id,
            tempName
          })
        }
      }

      // 如果没有找到，则检查 outputList 数组 = 大模型节点
      // if (node.type === BlockEnum.LLM && node.outputList) {
      if (node.outputList) {
        const outputMatch = node.outputList.find(o => o.variable === variable)
        if (outputMatch) {
          matchedVariables.push({
            ...outputMatch,
            nodeId: id,
            tempName
          })
        }
      }
    }
  })

  // 去重
  return matchedVariables.filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
}

/* id生成 */
/** 生成 UUID v4（浏览器原生 crypto） */
export function generateUUIDv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16 | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/* 输入id匹配 获取前缀title */
/** 将 'nodeId.xxx' 形式替换为 'nodeTitle.xxx' */
export const replaceIdWithTitle = (inputStr, flow) => {
  if (!inputStr || typeof inputStr !== 'string') return inputStr // 如果输入无效直接返回

  if (!inputStr.includes('.')) return inputStr // 如果没有"."直接返回

  const [idPart, rest] = inputStr.split('.', 2) // 分割字符串
  const node = Object.values(flow).find((n: any) => n.id === idPart) as any // 查找匹配的节点

  if (node && node.title) {
    return `${node.title}.${rest}` // 替换为"title.rest"
  }
  return inputStr // 如果找不到匹配的节点，保持原样
}

/**
 * 将日期字符串从ISO格式转换为自定义格式
 */
/** ISO 日期字符串格式化为 'YYYY-MM-DD HH:mm'（基于 moment） */
export const formatDateString = (dateString?: string) => {
  // 检查是否引入了moment库
  if (!dateString) {
    return ''
  }

  // 使用moment解析日期字符串并格式化
  return moment(dateString).format('YYYY-MM-DD HH:mm')
}

/* 获取单点登录临时凭证=联想pc需要 */
/**
 * 获取联想 PC 单点登录临时凭证（encode-baiying-token）
 * - 从 localStorage 读取 'lenovo-token' 作为请求头 Token
 * - 返回空串表示失败
 */
export const getLenovoPcToken = async (baiyingApiUrl: any): Promise<string> => {
  const envInfo = {
    baiyingApiUrl: baiyingApiUrl || 'https://paastest.baiying.com.cn'
  }

  try {
    const response = await customFetch(
      `${envInfo.baiyingApiUrl}/login-server/getAuthorCodeVal?loginTerminal=enterprise-management-backend`,
      {
        method: 'GET',
        headers: {
          Token: localStorage.getItem('lenovo-token')
        }
      }
    )

    if (!response.ok) {
      console.warn('联想 token 请求失败:', response.status)
      return ''
    }

    const result = await response.json()

    if (result.success && result.code === 200 && result.data) {
      const token = result.data
      console.log('获取联想 token 成功:', token)
      return token
    } else {
      console.warn('联想 token 接口返回失败:', result)
      return ''
    }
  } catch (error) {
    console.error('获取联想 token 异常:', error)
    return ''
  }
}

class EventEmitter {
  private events: { [key: string]: Function[] } = {}

  on(event: string, listener: Function): void {
    if (!this.events[event]) {
      this.events[event] = []
    }
    this.events[event].push(listener)
  }

  once(event: string, listener: Function): void {
    const onceListener = (...args: any[]) => {
      listener(...args)
      this.off(event, onceListener)
    }
    this.on(event, onceListener)
  }

  emit(event: string, ...args: any[]): boolean {
    if (!this.events[event]) return false
    this.events[event].forEach(listener => listener(...args))
    return true
  }

  off(event: string, listener: Function): void {
    if (!this.events[event]) return
    this.events[event] = this.events[event].filter(l => l !== listener)
  }

  removeAllListeners(event?: string): void {
    if (event) {
      delete this.events[event]
    } else {
      this.events = {}
    }
  }

  listenerCount(event: string): number {
    return this.events[event] ? this.events[event].length : 0
  }
}

export const BusEvent = new EventEmitter()
